// lib/microsite/retailLine.ts
//
// Súper online PR 2b (Tarea 2b.2): reglas PURAS del selector de peso y de variante
// de la tienda online. Sin React: lo que decide qué producto abre el selector, el
// paso y el tope del stepper, y la línea del carrito que se arma.
//
// La señal es la fila tal como la carga `loadMicrositeData`: `weight_unit` y
// `variants` solo existen con `online_retail_v2` (el select y la consulta de
// variantes se arman condicionales). Sin la bandera, ninguna fila trae esas claves y
// todo esto responde «no» → el camino de restaurante queda byte a byte el de main.
//
// Precio (R5): la línea guarda el precio CRUDO de la base —`items.price` para peso,
// `variant.price ?? item.price` para variante—, NUNCA `effectiveItemCardPrice`. La
// fórmula del carrito (`micrositeLineSubtotal`, lib/microsite/couponUx.ts) ya aplica
// el precio efectivo; guardar el ya efectivo mostraría $4,00/lb en un local con
// redondeo cuando el servidor cobra $3,99/lb.

import type { CartItem } from "@/components/microsite/types"
import { usesGuidedFlow } from "@/lib/menu/salesFlow"
import { effectiveItemCardPrice, effectiveUnitPrice, type PricingConfig } from "@/lib/utils/pricing"
import { ONLINE_WEIGHT_RULES, type OnlineWeightUnit } from "@/lib/retail/onlineWeight"

/** Variante activa del local (`item_variants`), tal como la cuelga el loader. */
export type OnlineVariant = {
  id: string
  item_id?: string
  name: string
  /** Precio de tarjeta de la variante; null = el del producto. */
  price: number | null
  is_active: boolean | null
  sort_order?: number | null
}

/** Campos retail de un ítem del micrositio. Todos opcionales: un plato no los trae. */
export type RetailPickerFacts = {
  pricing_mode?: string | null
  has_variants?: boolean | null
  /** Solo con `online_retail_v2`: ausente = la bandera está apagada. */
  weight_unit?: string | null
  /** Solo con `online_retail_v2`: ausente = la bandera está apagada. */
  variants?: OnlineVariant[] | null
}

/** Unidad de peso válida para la tienda online, o null. */
export function onlineWeightUnitOf(unit: string | null | undefined): OnlineWeightUnit | null {
  return typeof unit === "string" && Object.hasOwn(ONLINE_WEIGHT_RULES, unit) ? (unit as OnlineWeightUnit) : null
}

/** Producto por peso que llegó con la bandera (la fila trae la clave `weight_unit`). */
export function isRetailWeightItem(item: RetailPickerFacts): boolean {
  return item.pricing_mode === "weight" && item.weight_unit !== undefined
}

/** Producto con variantes que llegó con la bandera (la fila trae `variants`). */
export function isRetailVariantItem(item: RetailPickerFacts): boolean {
  return !isRetailWeightItem(item) && item.has_variants === true && Array.isArray(item.variants)
}

/** ¿Este producto se pide con el selector de peso o de variante? */
export function needsRetailPicker(item: RetailPickerFacts): boolean {
  return isRetailWeightItem(item) || isRetailVariantItem(item)
}

/**
 * ¿El toque en la carta abre el modal en vez de agregar directo? Un plato con
 * modificadores (lo de main: `hasModifiers`) o un producto por peso / con variantes.
 */
export function tapOpensItemSheet(
  item: RetailPickerFacts & { item_modifier_groups?: ReadonlyArray<{ modifier_groups: unknown }> | null },
): boolean {
  if (item.item_modifier_groups?.some((g) => g.modifier_groups != null)) return true
  return needsRetailPicker(item)
}

/**
 * Qué modal se abre: el flujo guiado no sabe de peso ni de variantes, así que lo
 * retail va siempre al clásico (ItemModal). Para un plato es exactamente
 * `usesGuidedFlow`.
 */
export function micrositeOpensGuidedFlow(
  salesFlow: string | null | undefined,
  item: RetailPickerFacts & Parameters<typeof usesGuidedFlow>[1],
): boolean {
  if (needsRetailPicker(item)) return false
  return usesGuidedFlow(salesFlow, item)
}

// ── La tarjeta de la carta (Tarea 2b.3, R6) ─────────────────────────────────

/**
 * Producto con variantes que llegó con la bandera y no tiene NINGUNA activa
 * (`variants: []`: el loader solo cuelga las activas). La tarjeta dice «Agotado» y
 * el toque no abre un selector vacío. Un plato nunca: no es `isRetailVariantItem`.
 */
export function retailVariantsSoldOut(item: RetailPickerFacts): boolean {
  return isRetailVariantItem(item) && !(item.variants ?? []).some((v) => v.is_active !== false)
}

/**
 * ¿La tarjeta dice «Agotado» y el toque no abre el selector? Lo de arriba (variantes
 * sin ninguna activa) o un producto por peso con `weight_unit` nula o inválida
 * (minor 2 de la revisión final): su selector no tendría peso que pedir y el modal
 * quedaría con «Agregar» apagado, sin salida. Un plato nunca: no trae la clave.
 */
export function retailPickerSoldOut(item: RetailPickerFacts): boolean {
  if (isRetailWeightItem(item)) return onlineWeightUnitOf(item.weight_unit) === null
  return retailVariantsSoldOut(item)
}

/**
 * Precio que pinta la tarjeta de un producto retail, o null para todo lo demás (el
 * llamador sigue con `effectiveItemCardPrice` de siempre):
 *   - por peso: precio por unidad SIN el redondeo de $0,50, como lo cobra el
 *     servidor, + la unidad («$3.99/lb»);
 *   - con variantes: la variante activa más barata con su precio de tarjeta efectivo,
 *     con `from` («desde $2.50») solo si las variantes no cuestan todas lo mismo.
 */
export function retailCardPrice(
  item: RetailPickerFacts & { price: number },
  cfg: PricingConfig,
): { cardPrice: number; unit?: OnlineWeightUnit; from?: true } | null {
  if (isRetailWeightItem(item)) {
    const unit = onlineWeightUnitOf(item.weight_unit)
    return unit ? { cardPrice: effectiveUnitPrice(item.price, cfg), unit } : null
  }
  if (isRetailVariantItem(item)) {
    const prices = (item.variants ?? [])
      .filter((v) => v.is_active !== false)
      .map((v) => effectiveItemCardPrice(v.price ?? item.price, cfg))
    if (prices.length === 0) return null
    const min = Math.min(...prices)
    return prices.some((p) => p !== min) ? { cardPrice: min, from: true } : { cardPrice: min }
  }
  return null
}

// ── Stepper de peso ──────────────────────────────────────────────────────────

/** El stepper arranca en un paso (= el mínimo). */
export function initialWeightMilli(unit: OnlineWeightUnit): number {
  return ONLINE_WEIGHT_RULES[unit].stepMilli
}

/** Un toque de «−» (−1) o «+» (+1): nunca baja de un paso ni pasa del tope. */
export function stepWeightMilli(current: number, dir: 1 | -1, unit: OnlineWeightUnit): number {
  const { stepMilli, maxMilli } = ONLINE_WEIGHT_RULES[unit]
  return Math.min(maxMilli, Math.max(stepMilli, current + dir * stepMilli))
}

/** Mismo chequeo que el servidor (`validateOnlineWeight`): múltiplo del paso, ≥ paso, ≤ tope. */
function weightAllowed(weightMilli: number | undefined, unit: OnlineWeightUnit): weightMilli is number {
  const { stepMilli, maxMilli } = ONLINE_WEIGHT_RULES[unit]
  return (
    typeof weightMilli === "number" &&
    Number.isInteger(weightMilli) &&
    weightMilli >= stepMilli &&
    weightMilli <= maxMilli &&
    weightMilli % stepMilli === 0
  )
}

// ── Variantes ────────────────────────────────────────────────────────────────

/** Variantes en el orden del dueño, con `disabled` si están inactivas o agotadas. */
export function selectableVariants(
  variants: readonly OnlineVariant[] | null | undefined,
  soldOutVariantIds?: ReadonlySet<string>,
): Array<OnlineVariant & { disabled: boolean }> {
  return [...(variants ?? [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((v) => ({ ...v, disabled: v.is_active === false || soldOutVariantIds?.has(v.id) === true }))
}

// ── La línea del carrito ─────────────────────────────────────────────────────

/**
 * Línea del carrito para un producto por peso o con variante: cantidad 1, sin
 * modificadores (el servidor rechaza retail con modificadores), precio CRUDO (R5).
 * null si la elección no es válida: nunca se arma una línea que /session rechazaría.
 */
export function buildRetailCartLine(args: {
  item: RetailPickerFacts & { id: string; name: string; price: number }
  cartId: string
  weightMilli?: number
  variantId?: string | null
  note?: string
  soldOutVariantIds?: ReadonlySet<string>
}): CartItem | null {
  const { item, cartId, weightMilli, variantId, note, soldOutVariantIds } = args
  const specialInstructions = note?.trim() || undefined
  const base = {
    cartId,
    itemId: item.id,
    name: item.name, // CANÓNICO: el sufijo de peso/variante lo pone micrositeLineName
    quantity: 1,
    modifiers: [] as CartItem["modifiers"],
    ...(specialInstructions ? { specialInstructions } : {}),
  }

  if (isRetailWeightItem(item)) {
    const unit = onlineWeightUnitOf(item.weight_unit)
    if (!unit || !weightAllowed(weightMilli, unit)) return null
    return { ...base, price: item.price, weightMilli, weightUnit: unit }
  }

  if (isRetailVariantItem(item)) {
    const v = selectableVariants(item.variants, soldOutVariantIds).find((x) => x.id === variantId)
    if (!v || v.disabled) return null
    return { ...base, price: v.price ?? item.price, variantId: v.id, variantName: v.name }
  }

  return null
}
